// src/components/IncomingOrders/OrderInspector.jsx
import { useEffect, useState, useMemo } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu, 
  MenuItem, 
  ListItemText, 
  ListItemIcon, 
  Paper,
  Stack,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SendIcon from "@mui/icons-material/Send";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { PRINT_ORDER_HISTORY } from "../../graphql/queries";
import { useAddressLabels } from "./hooks/useAddressLabels";
import { useEditableOrder } from "./hooks/useEditableOrder";
import OrderDetail from "./OrderDetail";
import SplitButton from "../common/SplitButton";
import ReplyModal from "./ReplyModal";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const saveOptions = [
  {
    //type: "", (Skicka ingen type för vanlig spara med ev. action)
    label: "Spara med åtgärd",
    icon: (
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
        }}
      >
        <SaveOutlinedIcon fontSize="small" />

        {/* Premium badge */}
        <Box
          sx={{
            position: "absolute",
            bottom: -3,
            right: -3,
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: "#FACC15", // mjuk premium-gul
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
          }}
        >
          <BoltOutlinedIcon
            sx={{
              fontSize: 14,
              color: "#1E293B",
            }}
          />
        </Box>
      </Box>
    ),
  },
  {
    type: "NONE",
    label: "Spara utan åtgärd",
    icon: <SaveOutlinedIcon fontSize="small" />,
  },
];

const ACTION_LABELS = {
  NONE: "Ingen åtgärd",
  SEND_DELIVERY_NOTICE: "Skicka leveransavisering",
  SEND_ORDER_CONFIRMATION: "Skicka orderbekräftelse",
};

const MANUAL_ACTIONS = [
  "SEND_ORDER_CONFIRMATION",
  "SEND_DELIVERY_NOTICE",
];

// TODO: koppla till din verkliga data
const getManualActions = (order) => {
  // Exempel: om du senare lägger på order.manualActions = ["SEND_ORDER_CONFIRMATION", ...]
  // return order?.manualActions || [];

  // Tillfälligt: visa alla möjliga för demo
  return MANUAL_ACTIONS;
};

const MIN_WIDTH = 600;
const MAX_WIDTH = 900;
const INITIAL_WIDTH = MIN_WIDTH;

const paperWidthConstraints = {
  minWidth: MIN_WIDTH,
  maxWidth: "90vw",
};

const headerIconSx = {
  width: 32,
  height: 32,
  color: "text.secondary",
  opacity: 0.85,
  "&:hover": { opacity: 1 },
};

const OrderInspector = ({
  order,
  orderError,
  orderLoading,
  orderUpdating,
  orderUpdateError,
  onClose,
  onUpdateOrder,
  onWidthChange,
  width = INITIAL_WIDTH,
  onOpenStatusManager
}) => {
  const [addressView, setAddressView] = useState(() => {
    try {
      const v = localStorage.getItem("orderAddressView");
      if (v === "compact" || v === "compare" || v === "full") return v;
      return "compact";
    } catch {
      return "compact";
    }
  });

  const {
    editableOrder,
    getPayload,
    isDirty,
    isFieldDirty,
    isObjectDirty,
    resetDirty,
    updateField,
  } = useEditableOrder(order);

  const { getLabel: getAddressLabel } = useAddressLabels(order?.sortedAddressFields)

  const [printOrderHistory, { data: orderHistoryPrintData }] = useLazyQuery(
    PRINT_ORDER_HISTORY
  );

  const handlePrintOrder = () => {
    const id = order?.supplierOrderId;
    if (!id) return;

    printOrderHistory({
      variables: { supplierOrderIds: [id] },
    });
  };

  const handleSaveActions = (action) => {
    const supplierOrderId = order?.supplierOrderId;
    if (!supplierOrderId) return;

    const patch = getPayload();
    patch.performAction = action.type;
    

    onUpdateOrder({ supplierOrderId, patch });

  }

  const handleSave = () => {
    const supplierOrderId = order?.supplierOrderId;
    if (!supplierOrderId) return;

    const patch = getPayload();

    onUpdateOrder({ supplierOrderId, patch });
  };

  const [dragging, setDragging] = useState(false);
  const [internalWidth, setInternalWidth] = useState(width);
  const [deliveryNoticeSentAt, setDeliveryNoticeSentAt] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
const [executing, setExecuting] = useState(false);

const manualActions = useMemo(() => getManualActions(order), [order]);
const hasManualActions = manualActions?.length > 0;

const openActionMenu = (e) => setActionAnchorEl(e.currentTarget);
const closeActionMenu = () => setActionAnchorEl(null);

const runActions = async (types) => {
  const supplierOrderId = order?.supplierOrderId;
  if (!supplierOrderId) return;

  try {
    setExecuting(true);

    // Kör sekventiellt, så du får deterministisk ordning
    for (const type of types) {
      // Om din backend inte vill ha NONE, hoppa över
      if (!type || type === "NONE") continue;

      // Här skickar vi en ren action-patch (utan att spara formulärfält)
      await onUpdateOrder({
        supplierOrderId,
        patch: { performAction: type },
      });
    }
  } finally {
    setExecuting(false);
    closeActionMenu();
  }
};

const handleRunOne = (type) => runActions([type]);
const handleRunAll = () => runActions(manualActions);

  const handleSendDeliveryNotice = () => {
    setDeliveryNoticeSentAt(new Date());
    setSnackbarOpen(true);
  };

  useEffect(() => {
    try {
      localStorage.setItem("orderAddressView", addressView);
    } catch { }
  }, [addressView]);

  useEffect(() => {
    if (orderHistoryPrintData?.orderHistoryPrint) {
      window.open(orderHistoryPrintData.orderHistoryPrint);
    }
  }, [orderHistoryPrintData]);

  // Håll intern bredd i synk med prop
  useEffect(() => {
    setInternalWidth(width);
  }, [width]);

  useEffect(() => {
    setDeliveryNoticeSentAt(order?.deliveryNoticeSentAt ?? null);
  }, [order]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleMouseUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (onWidthChange) {
      onWidthChange(internalWidth);
    }
  };

  const handleMouseMove = (event) => {
    if (!dragging) return;

    const newWidth = clamp(window.innerWidth - event.clientX, MIN_WIDTH, MAX_WIDTH);
    setInternalWidth(newWidth);
  };

  useEffect(() => {
    if (!dragging) return;

    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  const DeliveryConfirmedIcon = () => (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      {/* Outline-version bakom */}
      <SendIcon
        sx={{
          position: "absolute",
          bottom: -2,
          right: -2,
          fontSize: 10,
          color: "transparent",
          "& path": {
            stroke: "white",
            strokeWidth: 9,
            strokeLinecap: "round",
            strokeLinejoin: "round",
          },
        }}
      />

      {/* Originalikonen ovanpå */}
      <SendIcon
        sx={{
          position: "absolute",
          bottom: -2,
          right: -2,
          fontSize: 10,
          color: "#16A34A",
        }}
      />

      {/* LocalShipping bakom allt */}
      <LocalShippingOutlinedIcon fontSize="small" />
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {/* Själva panelen med orderdetaljer */}
      <Paper
        elevation={2}
        sx={{
          width: internalWidth,
          ...paperWidthConstraints,
          height: "100%",
          p: 2,
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          {/* Vänster: titel + vyväljare */}
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h6" fontWeight={700}>
                Orderdetaljer
              </Typography>

              {order?.supplierOrderNo != null && (
                <Typography variant="body2" color="text.secondary">
                  #{order.supplierOrderNo}
                </Typography>
              )}
            </Stack>

            {/* VYVÄLJARE */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "rgba(0,0,0,0.025)",
                borderRadius: 1,
                p: "1px",
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={addressView}
                onChange={(e, v) => v && setAddressView(v)}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 1,
                    width: 24,
                    height: 20,
                    p: 0,
                    color: "text.secondary",
                    opacity: 0.65,
                  },
                  "& .MuiToggleButton-root svg": {
                    fontSize: "0.9rem",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "rgba(0,0,0,0.06)",
                    color: "text.primary",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                    opacity: 1,
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "background.paper",
                  },
                }}
              >
                <ToggleButton value="compact">
                  <ViewAgendaIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="compare">
                  <TableRowsIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="full">
                  <ViewColumnIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

            </Box>
          </Stack>

          {/* Höger: actions */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              "& > *": {
                ml: 1,
              },
              "& > *:first-of-type": {
                ml: 0,
              },
            }}
          >
            <SplitButton 
              disabled={orderUpdating || orderLoading || orderError}
              onClick={handleSaveActions} options={saveOptions}
              placement="bottom-end"
            />
            
            <Tooltip
              title={isDirty ? "Ordern måste sparas först" : (hasManualActions ? "Utför åtgärd" : "Inga åtgärder att utföra")}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={openActionMenu}                  
                  disabled={isDirty || !hasManualActions || executing}
                  sx={[
                    headerIconSx,
                    {
                      ml: 1,
                      mr: -0.5,
                    },
                  ]}
                >
                  {executing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <BoltOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Menu
              anchorEl={actionAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(actionAnchorEl)}
              onClose={closeActionMenu}
              slotProps={{ paper: { sx: { borderRadius: 2 } } }}
            >
              {manualActions.map((type) => (
                <MenuItem
                  key={type}
                  onClick={() => handleRunOne(type)}
                  disabled={executing}
                  sx={{
                    py: 1,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: "primary.main",
                      mr: 0.5,
                    }}
                  >
                    {type === "SEND_ORDER_CONFIRMATION" && (
                      <FactCheckOutlinedIcon fontSize="small" />
                    )}

                    {type === "SEND_DELIVERY_NOTICE" && (
                      <DeliveryConfirmedIcon />
                    )}
                  </ListItemIcon>

                  <ListItemText
                    primary={ACTION_LABELS[type] || type}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>

            <Tooltip title="Skicka meddelande" arrow>
              <IconButton
                size="small"
                // TODO: koppla in din befintliga handler här om du har en
                sx={headerIconSx}
                onClick={e => setOpenMessageDialog(true)}
              >
                <MailOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <ReplyModal supplierOrderId={order?.supplierOrderId} open={openMessageDialog} onClose={n=>setOpenMessageDialog(false)} />

            <Tooltip title="Skriv ut order" arrow>
              <IconButton
                size="small"
                onClick={handlePrintOrder}
                sx={headerIconSx}
              >
                <PrintOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        {/* Innehåll */}
        <Box sx={{ flex: 1, overflow: "auto", pr: 1 }}>
          {editableOrder && !orderLoading && !orderError &&
            <OrderDetail
              order={editableOrder}
              width={width}
              addressView={addressView}
              setAddressView={setAddressView}
              updateField={updateField}
              isFieldDirty={isFieldDirty}
              isObjectDirty={isObjectDirty}
              getAddressLabel={getAddressLabel}
              onOpenStatusManager = { onOpenStatusManager}
            />
          }
          {orderError && !orderLoading &&
            <Typography variant="body2" color="error">
              Kan inte visa detaljer för vald order.
            </Typography>
          }
          {orderLoading &&
            <Typography variant="body2" color="text.secondary">
              Hämtar order...
            </Typography>
          }
        </Box>
      </Paper>

      {/* Vertikalt draghandtag (resizable) */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: "absolute",
          left: -3,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "ew-resize",
          backgroundColor: "#f7bd02",
          opacity: 0.6,
          "&:hover": { opacity: 1 },
          borderRadius: "4px",
        }}
      />
    </Box>
  );
};

OrderInspector.displayName = "OrderInspector";

export default OrderInspector;
